'use client'

import dynamic from "next/dynamic"
import { CrouselDataProps } from "@/util/enum"
import { HomeBannerSection } from "@/types/GetHomePageLayoutApiType"
import type { CSSProperties } from "react"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import SidebarSection from "./SidebarSection"
import HomeBannerStaticHero from "./HomeBannerStaticHero"

const HomeBannerSwiperDynamic = dynamic(() => import("./HomeBannerSwiperClient"), {
  ssr: false,
})

const DESKTOP_FALLBACK_URL = "/images/home_data/desktop_banner.jpg"
const MOBILE_FALLBACK_URL = "/images/home_data/mobile_banner.png"

const STATIC_FALLBACK_SLIDE: CrouselDataProps = {
  url: DESKTOP_FALLBACK_URL,
  alt_name: "banner_image",
  mobile_url: MOBILE_FALLBACK_URL,
}

interface HomeCrouselSectionProps {
  crousal_data: HomeBannerSection
  mobile_data: HomeBannerSection
}

const HomeCrouselSection = ({ crousal_data, mobile_data }: HomeCrouselSectionProps) => {

  const desktopImages = useMemo(
    () =>
      Array.isArray(crousal_data?.images) && crousal_data?.images?.length
        ? crousal_data.images.map((curr) => ({
            url: curr?.bannerImage?.url || DESKTOP_FALLBACK_URL,
            alt: curr?.bannerImage?.alt || "banner_image",
            linkTo: curr?.linkTo || null,
          }))
        : [],
    [crousal_data?.images]
  )

  const mobileImages = useMemo(
    () =>
      Array.isArray(mobile_data?.images) && mobile_data?.images?.length
        ? mobile_data.images.map((curr) => ({
            url: curr?.bannerImage?.url || MOBILE_FALLBACK_URL,
            alt: curr?.bannerImage?.alt || "banner_image",
            linkTo: curr?.linkTo || null,
          }))
        : [],
    [mobile_data?.images]
  )

  /**
   * Pair desktop + mobile slides index-by-index into a single carousel list.
   * The `<picture>` art direction inside `ImageComponent` then picks the right
   * asset per viewport — no JS breakpoint, no flash, no `useIsMobile` swap.
   * - When mobile array is shorter, the missing entries get `mobile_url=null`
   *   and the desktop URL is used on mobile too (graceful fallback).
   * - When mobile array is longer, those extra slides use the mobile URL as
   *   the desktop fallback as well so they still render on desktop.
   */
  const carouselSlides = useMemo((): CrouselDataProps[] => {
    const len = Math.max(desktopImages.length, mobileImages.length)
    if (len === 0) return []

    const slides: CrouselDataProps[] = []
    for (let i = 0; i < len; i++) {
      const desktop = desktopImages[i]
      const mobile = mobileImages[i]
      slides.push({
        url: desktop?.url || mobile?.url || DESKTOP_FALLBACK_URL,
        alt_name: desktop?.alt || mobile?.alt || "banner_image",
        mobile_url: mobile?.url || null,
        linkTo: desktop?.linkTo || mobile?.linkTo || null,
      })
    }
    return slides
  }, [desktopImages, mobileImages])

  const bannerSectionRef = useRef<HTMLDivElement>(null)
  const [bannerSectionHeight, setBannerSectionHeight] = useState(0)

  const staticHeroSlide = useMemo(
    () => carouselSlides[0] ?? STATIC_FALLBACK_SLIDE,
    [carouselSlides]
  )

  const [mounted, setMounted] = useState(false)
  const [swiperReady, setSwiperReady] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useLayoutEffect(() => {
    const el = bannerSectionRef.current
    if (!el) return

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const h = Math.round(entry.contentRect.height)
      setBannerSectionHeight((prev) => (prev !== h ? h : prev))
    })

    ro.observe(el)
    return () => ro.disconnect()
  }, [mounted, swiperReady])

  if (!carouselSlides.length) return null

  const showStaticPlaceholder = !mounted || !swiperReady

  return (
    <>
      <SidebarSection bannerMinHeightPx={bannerSectionHeight} />
      <div className="flex flex-col lg:flex-row w-full px-0 md:px-10 lg:px-0 h-full">
        <div className="hidden lg:flex w-[6%] h-0!"></div>
        <div
          ref={bannerSectionRef}
          className="w-full lg:w-[94%] pt-0 md:pt-2 pr-0 lg:pr-16 pl-0 lg:pl-8 h-fit"
          style={
            bannerSectionHeight
              ? ({ ["--home-banner-section-height"]: `${bannerSectionHeight}px` } as CSSProperties)
              : undefined
          }
        >
          <div className="relative w-full">
            {showStaticPlaceholder ? (
              <HomeBannerStaticHero slide={staticHeroSlide} />
            ) : null}
            {mounted ? (
              <div
                className={
                  swiperReady
                    ? "relative w-full min-h-0 [&_.swiper]:h-auto [&_.swiper-wrapper]:h-auto"
                    : "absolute inset-0 w-full opacity-0 pointer-events-none min-h-0 [&_.swiper]:h-auto [&_.swiper-wrapper]:h-auto"
                }
              >
                <HomeBannerSwiperDynamic
                  crouselData={carouselSlides}
                  onSwiperReady={() => setSwiperReady(true)}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

export default HomeCrouselSection
